import { useOperationalDestinations } from '../../../hooks/useOperationalDestinations';
import { useConsolidatedPurchases } from '../../supply-orders/hooks/useConsolidatedPurchases';
import { useMaterialScopeOptions } from '../../hooks/useMaterialScopeOptions';
import { useMaterialGroups } from '../../../hooks/useMaterialGroups';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useCallback, useMemo, useState } from 'react';
import {
  useExternalOrders,
  useExternalOrderFilterOptions,
  useExternalOrder,
  useAuthorizeExternalOrder,
  useCreateExternalOrder,
  useCheckExternalOrderStock,
  useCancelExternalOrder,
  useSetExternalOrderConsumptionAuthorization,
} from '../../../hooks/useExternalOrders';
import { useWarehouses, useWarehouse } from '../../../hooks/useWarehouses';
import { useProjectOptions, useProject } from '../../../hooks/useProjects';
import {
  ITEMS_PER_PAGE,
  INITIAL_FILTERS,
  getInitialOrderState,
} from '../constants/internalOrdersConstants';
import {
  buildCreateOrderPayload,
  mapExternalOrderToDisplay,
} from '../utils/internalOrdersUtils';

export const useInternalOrdersTable = () => {
  const { data: operationalDestinations = [] } = useOperationalDestinations();
  const scopeOptions = useMaterialScopeOptions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(getInitialOrderState);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const tableSort = useTableSort(() => setPage(1));

  const [expandedId, setExpandedId] = useState(null);
  const expandedQuery = useExternalOrder(expandedId);
  const [groupSearch, setGroupSearch] = useState('');
  const groupQuery = useMaterialGroups(
    { search: groupSearch, per_page: 25 },
    { enabled: dialogOpen }
  );
  /**
   * Deja en el pedido exactamente los grupos elegidos: agrega los materiales de los grupos nuevos
   * y quita los de los grupos que se sacaron; los materiales sueltos no se tocan.
   * @param {Array<string>} groupIds UUID de los grupos seleccionados.
   */
  const changeGroups = (groupIds = []) => {
    setSelectedOrder((prev) => {
      const selected = new Set(groupIds);
      const current = new Set(prev.materials.map((x) => x.uuid_material_group).filter(Boolean));
      const kept = prev.materials.filter(
        (x) => !x.uuid_material_group || selected.has(x.uuid_material_group)
      );
      const added = groupIds
        .filter((groupId) => !current.has(groupId))
        .map((groupId) => groupQuery.data?.items?.find((x) => x.uuid === groupId))
        .filter(Boolean)
        .flatMap((group) =>
          group.articles.map((article) => ({
            materialId: article.uuid,
            lineId: `${group.uuid}:${article.uuid}`,
            code: article.code,
            name: article.name,
            unit: article.unit,
            quantity: 1,
            uuid_material_group: group.uuid,
            group_expected_revision: group.revision,
            group_name: group.name,
          }))
        );
      return { ...prev, materials: [...kept, ...added] };
    });
  };
  const [numberSearch, searchNumber] = useState('');
  const [creatorSearch, searchCreator] = useState('');
  const numbersQuery = useExternalOrderFilterOptions('order_number', numberSearch);
  const creatorsQuery = useExternalOrderFilterOptions('created_by', creatorSearch);
  const orderFilterOptions = {
    numbers: numbersQuery.data?.items || [],
    creators: creatorsQuery.data?.items || [],
    searchNumber,
    searchCreator,
  };

  const {
    data: ordersResponse,
    isLoading: ordersLoading,
  } = useExternalOrders({
    page,
    per_page: rowsPerPage,
    search: searchTerm,
    ...filters,
    ...tableSort.sortParams,
  });
  const {
    data: detailOrder,
    isLoading: detailOrderLoading,
    refetch: refetchDetailOrder,
  } = useExternalOrder(selectedOrderId, {
    enabled: detailDialogOpen && Boolean(selectedOrderId),
  });
  const { data: detailProject } = useProject(detailOrder?.uuid_project, {
    enabled: detailDialogOpen && Boolean(detailOrder?.uuid_project),
  });
  const { data: detailWarehouse } = useWarehouse(
    detailOrder?.uuid_destination_warehouse,
    {
      enabled: detailDialogOpen && Boolean(detailOrder?.uuid_destination_warehouse),
    }
  );
  const { data: warehouses = [], isLoading: warehousesLoading } = useWarehouses({
    enabled: true,
  });
  const { data: projects = [], isLoading: projectsLoading } = useProjectOptions({
    enabled: true,
  });

  const createOrderMutation = useCreateExternalOrder();
  const authorizeOrderMutation = useAuthorizeExternalOrder();
  const checkStockMutation = useCheckExternalOrderStock();
  const consumptionAuthorizationMutation = useSetExternalOrderConsumptionAuthorization();
  const cancelOrderMutation = useCancelExternalOrder();

  const orders = useMemo(() => {
    if (Array.isArray(ordersResponse)) return ordersResponse;
    return ordersResponse?.results || [];
  }, [ordersResponse]);

  const data = useMemo(
    () => orders.map((order) => mapExternalOrderToDisplay(order, [], [])),
    [orders]
  );
  const consolidation = useConsolidatedPurchases(filters, searchTerm, data);

  const totalCount = Array.isArray(ordersResponse)
    ? data.length
    : ordersResponse?.total || 0;
  const totalPages = Math.max(
    1,
    Array.isArray(ordersResponse)
      ? Math.ceil(totalCount / rowsPerPage)
      : ordersResponse?.pages || 1
  );

  const selectedOrderDetail = useMemo(() => {
    if (!detailDialogOpen || !selectedOrderId || !detailOrder) return null;

    return mapExternalOrderToDisplay(detailOrder, [], [], {
      warehouse: detailWarehouse,
      project: detailProject,
    });
  }, [
    detailDialogOpen,
    selectedOrderId,
    detailOrder,
    detailWarehouse,
    detailProject,
  ]);

  const isLoading = ordersLoading;
  const isSaving = createOrderMutation.isPending;
  const isProcessing =
    authorizeOrderMutation.isPending ||
    checkStockMutation.isPending ||
    consumptionAuthorizationMutation.isPending ||
    cancelOrderMutation.isPending;

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleOpenDialog = useCallback(() => {
    setSelectedOrder(getInitialOrderState());
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedOrder(getInitialOrderState());
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'destination_kind'
        ? {
            uuid_project: '',
            uuid_operational_destination: '',
            internal_purpose: '',
            internal_purpose_detail: '',
          }
        : {}),
    }));
  }, []);

  const handleAddMaterial = useCallback((material) => {
    if (!material?.value) return;

    setSelectedOrder((prev) => {
      const exists = (prev.materials || []).some(
        (item) => item.materialId === material.value && !item.uuid_material_group
      );
      if (exists) return prev;

      return {
        ...prev,
        materials: [
          ...(prev.materials || []),
          {
            materialId: material.value,
            lineId: material.value,
            code: material.code,
            name: material.name,
            unit: material.unit,
            quantity: 1,
          },
        ],
      };
    });
  }, []);

  const handleUpdateMaterialQuantity = useCallback((materialId, quantity) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).map((item) =>
        (item.lineId || item.materialId) === materialId
          ? { ...item, quantity }
          : item
      ),
    }));
  }, []);

  const handleRemoveMaterial = useCallback((materialId) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).filter(
        (item) => (item.lineId || item.materialId) !== materialId
      ),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const payload = buildCreateOrderPayload(selectedOrder);
    if (!payload) return false;

    const createdOrder = await createOrderMutation.mutateAsync(payload);
    const createdOrderId = createdOrder?.uuid;

    setDialogOpen(false);
    setSelectedOrder(getInitialOrderState());

    if (createdOrderId) {
      setSelectedOrderId(createdOrderId);
      setDetailDialogOpen(true);
    }

    return true;
  }, [selectedOrder, createOrderMutation]);

  const handleViewDetail = useCallback((order) => {
    setSelectedOrderId(order.uuid);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrderId(null);
  }, []);

  const handleCheckStock = useCallback(async (orderId) => {
    return checkStockMutation.mutateAsync(orderId);
  }, [checkStockMutation]);

  const handleAuthorizeOrder = useCallback(async (orderId) => {
    return authorizeOrderMutation.mutateAsync(orderId);
  }, [authorizeOrderMutation]);

  const handleSetConsumptionAuthorization = useCallback(async (orderId, value = true) => {
    return consumptionAuthorizationMutation.mutateAsync({
      id: orderId,
      consumption_authorized: value,
    });
  }, [consumptionAuthorizationMutation]);

  const handleRefreshDetailOrder = useCallback(async () => {
    await refetchDetailOrder();
  }, [refetchDetailOrder]);

  const handleCancelOrder = useCallback(async ({ id, reason }) => {
    return cancelOrderMutation.mutateAsync({ id, reason });
  }, [cancelOrderMutation]);

  return {
    consolidation,
    operationalDestinations,
    orderFilterOptions,
    scopeOptions,
    expandedId,
    expandedQuery,
    onExpand: (id) => setExpandedId((prev) => (prev === id ? null : id)),
    sort: tableSort.sort,
    onSort: tableSort.toggleSort,
    groupOptions: groupQuery.data?.items || [],
    groupSearch,
    setGroupSearch,
    changeGroups,
    data,
    isLoading,
    isSaving,
    isProcessing,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    filters,
    dialogOpen,
    detailDialogOpen,
    selectedOrder,
    selectedOrderDetail,
    detailOrderLoading,
    warehouses,
    projects,
    projectsLoading,
    warehousesLoading,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleAddMaterial,
    handleUpdateMaterialQuantity,
    handleRemoveMaterial,
    handleSave,
    handleViewDetail,
    handleCloseDetailDialog,
    handleAuthorizeOrder,
    handleCheckStock,
    handleSetConsumptionAuthorization,
    handleRefreshDetailOrder,
    handleCancelOrder,
  };
};
